// What a sale says on screen. v2 names the sale (`sale.label`) and sets its prices; the page only words them.
// The percentage comes from the prices, never from copy, so a milder sale (Halloween 2026: 30% plans, 60% gems)
// never shows the old hard-coded "70%".

/** Whole percent off `regular`, rounded down so the page never claims more than the price gives. Null without both,
 *  or under 1%, so a page never shows "0% Off". */
export function percentOff(regular, salePrice) {
  const before = Number(regular)
  const after = Number(salePrice)
  if (!(before > 0) || !(after >= 0) || after >= before) return null
  const percent = Math.floor(((before - after) / before) * 100 + 1e-9)
  return percent >= 1 ? percent : null
}

/** The smallest percent off among [regular, sale] pairs, so a banner's "Claim N% Off" holds for every item. */
export function leastPercentOff(pairs) {
  const percents = pairs.map(([regular, salePrice]) =>
    percentOff(regular, salePrice),
  )
  const known = percents.filter((percent) => percent !== null)
  return known.length ? Math.min(...known) : null
}

/** The plans' sale percentage from the upgrade page's answer (src/lib/plan-prices.js), or null. */
export function planSalePercent(answer) {
  const plans = Object.values(answer?.plans ?? {}).filter(
    (plan) => plan?.blackFridayPrice,
  )
  return leastPercentOff(
    plans.map((plan) => [plan.price, plan.blackFridayPrice]),
  )
}

/** The gem packs' sale percentage from the gems answer (src/lib/gem-prices.js), or null. */
export function gemSalePercent(answer) {
  if (!answer?.sale) return null
  return leastPercentOff(
    (answer.packs ?? []).map((pack) => [pack.list_price, pack.price]),
  )
}

// Each sale's words by its label in the price list. A label not here keeps the summer sale's words, as before.
const COPY = {
  Halloween: {
    emoji: '🎃',
    banner: 'halloweenSaleBanner',
    bannerShort: 'halloweenSaleBannerShort',
    header: 'halloweenSaleHeadline',
    plansSubHeader: 'halloweenPlansTagline',
    gemsSubHeader: 'halloweenGemsTagline',
  },
  'Black Friday': {
    emoji: '🖤',
    banner: 'blackFridaySaleBanner',
    bannerShort: 'blackFridaySaleBannerShort',
    header: 'blackFridaySaleHeadline',
    plansSubHeader: 'blackFridayPlansTagline',
    gemsSubHeader: 'blackFridayGemsTagline',
  },
}

const SUMMER = {
  emoji: '☀️',
  banner: 'blackFridayBanner',
  bannerShort: 'summerSaleTitleShort',
  header: 'discountHeader',
  plansSubHeader: 'discountSubHeader',
  gemsSubHeader: 'discountSubHeader',
}

/** The translation keys and emoji for a sale's label. */
export function saleCopy(label) {
  return COPY[label] ?? SUMMER
}

/** The emoji of a sale with its own words (Halloween's 🎃), or null: the summer words' ☀️ is no other sale's. */
export function saleEmoji(label) {
  return COPY[label]?.emoji ?? null
}

/** The header's offer for this plan (the phone badge, src/hooks/useOfferExperiment.js): Free gets the upgrade
 *  discount, a paid plan the gems discount. `salePercent` is a running sale's (planSalePercent or gemSalePercent);
 *  without one, the standing 20% intro and 50% subscriber price. `saleLabel` puts its emoji first ("🎃 -30%"). */
export function offerFor(displayPlan, salePercent, saleLabel) {
  if (!displayPlan) return null
  const emoji = salePercent ? saleEmoji(saleLabel) : null
  const badge = (percent) => (emoji ? `${emoji} -${percent}%` : `-${percent}%`)
  if (displayPlan === 'Free') {
    const percent = salePercent ?? 20
    return {
      id: 'upgrade_discount',
      label: badge(percent),
      href: '/upgrade',
      percent: `${percent}%`,
    }
  }
  // A gems discount keeps the banner's /gems destination, so both arms measure the same journey.
  const percent = salePercent ?? 50
  return {
    id: 'gems_discount',
    label: badge(percent),
    href: '/gems',
    percent: `${percent}%`,
  }
}
